<template>
   <div
      ref="mapContainer"
      class="rounded-small size-full overflow-hidden"
      tabindex="0"
      @keydown="onKeyDown"
   />
</template>

<script lang="ts" setup>
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import bbox from '@turf/bbox'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { mapConfig, mapIcons } from '~/utils/consts/spotfish-config'

type DrawMode = 'line_string' | 'polygon' | 'point'

type Emit = {
   create: [features: any[]]
   update: [features: any[]]
   delete: [features: any[]]
   select: [feature: any]
   change: [features: any[]]
}

type Props = {
   readonly?: boolean
   enabledModes?: DrawMode[]
   showControls?: boolean
   showMarkers?: boolean
   bboxIndex?: number
   features?: {
      id: string
      geojson: any
      bbox: any
      center?: any
      is_open?: boolean | null
      is_active?: boolean
      name?: string
      is_forbidden?: boolean
      is_editable?: boolean
   }[]
}
;(MapboxDraw.constants.classes as any).CONTROL_BASE = 'maplibregl-ctrl'
;(MapboxDraw.constants.classes as any).CONTROL_PREFIX = 'maplibregl-ctrl-'
;(MapboxDraw.constants.classes as any).CONTROL_GROUP = 'maplibregl-ctrl-group'

const props = withDefaults(defineProps<Props>(), {
   enabledModes: () => ['line_string', 'polygon'],
})
const emit = defineEmits<Emit>()

const mapContainer = ref<HTMLElement>()
const map = ref<maplibregl.Map>()
const draw = ref<MapboxDraw>()

const customMarkers = ref<maplibregl.Marker[]>([])

const onKeyDown = (e: KeyboardEvent) => {
   if (props.readonly || !draw.value) return

   if (e.key === 'Backspace' || e.key === 'Delete') {
      draw.value.trash()
   }
}

const loadMapIcons = async () => {
   if (!map.value || !mapIcons) return

   const iconPromises = Object.entries(mapIcons).map(([key, iconUrl]) => {
      return new Promise<void>((resolve) => {
         if (!iconUrl || map.value!.hasImage(key)) {
            resolve()
            return
         }

         const img = new Image()
         img.crossOrigin = 'anonymous'
         img.onload = () => {
            if (!map.value!.hasImage(key)) {
               map.value!.addImage(key, img)
            }
            resolve()
         }
         img.onerror = () => resolve()
         img.src = iconUrl
      })
   })

   await Promise.all(iconPromises)
}

const createCustomMarkerElement = (feature: any) => {
   const el = document.createElement('div')

   const bgClass = feature.is_active && !feature.is_forbidden ? 'bg-secondary' : 'bg-light-grey'

   el.className = `flex items-center gap-1 ${bgClass} rounded-full px-1 py-0.5 cursor-pointer`

   const icon = document.createElement('img')
   icon.src = feature.is_forbidden
      ? mapIcons?.locationForbidden
      : feature.is_active
        ? mapIcons?.location
        : mapIcons?.locationDisabled
   icon.className = 'size-4'

   const text = document.createElement('p')
   text.textContent = feature.name
   text.className = `p-s`

   el.appendChild(icon)
   el.appendChild(text)

   el.addEventListener('click', () => {
      emit('select', { properties: feature })
      if (feature.bbox?.coordinates) {
         const coords = feature.bbox.coordinates[0]
         const bounds = new maplibregl.LngLatBounds()
         coords.forEach((coord: [number, number]) => bounds.extend(coord))
         map.value!.fitBounds(bounds, { padding: 50 })
      }
   })

   return el
}

const addPointMarkers = async () => {
   if (!map.value || !props.features || !props.showMarkers) return

   customMarkers.value.forEach((marker) => marker.remove())
   customMarkers.value = []

   const centers = {
      type: 'FeatureCollection' as const,
      features: props.features
         .filter((feature) => {
            return feature.center && feature.center.coordinates
         })
         .map((feature) => ({
            type: 'Feature' as const,
            properties: {
               ...feature,
               geojson: undefined,
               center: undefined,
            },
            geometry: feature.center,
         })),
   }

   if (centers.features.length === 0) {
      return
   }

   map.value.addSource('markers-clustered', {
      type: 'geojson',
      data: centers,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 70,
   })

   map.value.addLayer({
      id: 'unclustered-points',
      type: 'circle',
      source: 'markers-clustered',
      filter: ['!', ['has', 'point_count']],
      paint: {
         'circle-radius': 0,
         'circle-opacity': 0,
      },
   })

   if (mapIcons?.locationCluster) {
      map.value.addLayer({
         id: 'clusters',
         type: 'symbol',
         source: 'markers-clustered',
         filter: ['has', 'point_count'],
         layout: {
            'icon-image': 'locationCluster',
            'icon-size': 0.6,
            'icon-allow-overlap': true,
            'text-field': '{point_count}',
            'text-font': ['Open Sans Regular'],
            'text-size': 14,
            'text-allow-overlap': true,
         },
         paint: {
            'icon-opacity': 1,
            'text-color': mapConfig?.primary,
         },
      })
   } else {
      map.value.addLayer({
         id: 'clusters',
         type: 'circle',
         source: 'markers-clustered',
         filter: ['has', 'point_count'],
         paint: {
            'circle-color': mapConfig?.primary,
            'circle-radius': 20,
            'circle-opacity': 1,
         },
      })

      map.value.addLayer({
         id: 'cluster-count',
         type: 'symbol',
         source: 'markers-clustered',
         filter: ['has', 'point_count'],
         layout: {
            'text-field': '{point_count}',
            'text-font': ['Open Sans Semibold'],
            'text-size': 14,
            'text-allow-overlap': true,
         },
         paint: {
            'text-color': '#ffffff',
         },
      })
   }

   map.value.on('click', 'clusters', (e) => {
      const features = map.value!.queryRenderedFeatures(e.point, {
         layers: ['clusters'],
      })
      const clusterId = features[0]?.properties?.cluster_id
      if (clusterId) {
         const source = map.value!.getSource('markers-clustered') as maplibregl.GeoJSONSource
         source.getClusterExpansionZoom(clusterId).then((zoom: number) => {
            map.value!.easeTo({
               center: (features[0].geometry as any).coordinates,
               zoom: zoom + 1,
               duration: 500,
            })
         })
      }
   })

   map.value.on('mouseenter', 'clusters', () => {
      map.value!.getCanvas().style.cursor = 'pointer'
   })
   map.value.on('mouseleave', 'clusters', () => {
      map.value!.getCanvas().style.cursor = ''
   })

   const updateVisibleMarkers = () => {
      if (!map.value || !props.features) return

      customMarkers.value.forEach((marker) => marker.remove())
      customMarkers.value = []

      const unclusteredFeatures = map.value.queryRenderedFeatures({
         layers: ['unclustered-points'],
      })

      unclusteredFeatures.forEach((feature) => {
         const featureId = feature.properties?.id
         const originalFeature = props.features?.find((f) => f.id === featureId)

         if (originalFeature?.name && feature.geometry.type === 'Point') {
            const el = createCustomMarkerElement(originalFeature)
            const marker = new maplibregl.Marker({ element: el })
               .setLngLat(feature.geometry.coordinates as [number, number])
               .addTo(map.value!)

            customMarkers.value.push(marker)
         }
      })
   }

   map.value.on('moveend', updateVisibleMarkers)
   map.value.on('zoomend', updateVisibleMarkers)
   map.value.on('render', updateVisibleMarkers)

   updateVisibleMarkers()
}

const addReadonlyGeometries = () => {
   if (!map.value || !props.features) return

   const readonlyFeatures = {
      type: 'FeatureCollection' as const,
      features: props.features
         .filter(
            (f) =>
               f.geojson && f.geojson !== '{}' && typeof f.geojson === 'object' && f.geojson.type
         )
         .map((f) => ({
            type: 'Feature' as const,
            properties: { ...f, id: f.id },
            geometry: f.geojson,
         })),
   }

   if (readonlyFeatures.features.length === 0) return

   map.value.addSource('readonly-features', {
      type: 'geojson',
      data: readonlyFeatures as any,
   })

   map.value.addLayer({
      id: 'readonly-features-fill',
      type: 'fill',
      source: 'readonly-features',
      filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
      paint: {
         'fill-color': mapConfig?.primary,
         'fill-opacity': 0.2,
      },
   })

   map.value.addLayer({
      id: 'readonly-features-line',
      type: 'line',
      source: 'readonly-features',
      paint: {
         'line-color': mapConfig?.primary,
         'line-width': 3,
      },
   })
}

const addGeometryToMap = (draw: any, geometry: any, id: string, isEditable: boolean = true) => {
   if (geometry.type === 'MultiLineString') {
      geometry.coordinates.forEach((lineCoords: any, i: number) => {
         draw.add({
            id: `${id}_${i}`,
            type: 'Feature',
            geometry: {
               type: 'LineString',
               coordinates: lineCoords,
            },
            properties: {
               id: `${id}_${i}`,
               is_editable: isEditable,
            },
         })
      })
   } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach((polygonCoords: any, i: number) => {
         draw.add({
            id: `${id}_${i}`,
            type: 'Feature',
            geometry: {
               type: 'Polygon',
               coordinates: polygonCoords,
            },
            properties: {
               id: `${id}_${i}`,
               is_editable: isEditable,
            },
         })
      })
   } else if (geometry.type === 'GeometryCollection') {
      geometry.geometries.forEach((nestedGeometry: any, i: number) => {
         addGeometryToMap(draw, nestedGeometry, `${id}_${i}`, isEditable)
      })
   } else {
      draw.add({
         id: id,
         type: 'Feature',
         geometry: geometry,
         properties: {
            id: id,
            is_editable: isEditable,
         },
      })
   }
}

const getBoundsFromBbox = (bbox: any, padding = 0.05): [number, number, number, number] | null => {
   if (!bbox?.coordinates?.[0]) return null

   const coords = bbox.coordinates[0]
   let minLng = coords[0][0],
      minLat = coords[0][1],
      maxLng = coords[0][0],
      maxLat = coords[0][1]

   coords.forEach(([lng, lat]: [number, number]) => {
      minLng = Math.min(minLng, lng)
      minLat = Math.min(minLat, lat)
      maxLng = Math.max(maxLng, lng)
      maxLat = Math.max(maxLat, lat)
   })

   return [minLng - padding, minLat - padding, maxLng + padding, maxLat + padding]
}

const initDrawControl = () => {
   if (!map.value || props.readonly) return

   const controls: any = {}
   if (props.showControls) {
      if (props.enabledModes.includes('line_string')) controls.line_string = true
      if (props.enabledModes.includes('polygon')) controls.polygon = true
      if (props.enabledModes.includes('point')) controls.point = true
      controls.trash = true
   }

   const DisabledSelect = {
      ...MapboxDraw.modes.simple_select,
      onClick: function (state: any, e: any) {
         const featureId = e.featureTarget?.properties?.id
         if (featureId) {
            const feature = (this as any).getFeature(featureId)
            if (feature && feature.properties.is_editable === false) {
               return
            }
         }
         return (MapboxDraw.modes.simple_select.onClick as any)?.call(this, state, e)
      },
      onMouseDown: function (state: any, e: any) {
         const featureId = e.featureTarget?.properties?.id
         if (featureId) {
            const feature = (this as any).getFeature(featureId)
            if (feature && feature.properties.is_editable === false) {
               return
            }
         }
         return (MapboxDraw.modes.simple_select.onMouseDown as any)?.call(this, state, e)
      },
   }

   const modes = {
      ...MapboxDraw.modes,
      simple_select: DisabledSelect,
   }

   if (draw.value) {
      map.value.removeControl(draw.value as any)
   }

   draw.value = new MapboxDraw({
      displayControlsDefault: false,
      controls,
      modes,
      styles: [
         {
            id: 'gl-draw-line',
            type: 'line',
            filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
            layout: {
               'line-cap': 'round',
               'line-join': 'round',
            },
            paint: {
               'line-color': mapConfig?.primary,
               'line-width': 3,
            },
         },
         {
            id: 'gl-draw-polygon-fill',
            type: 'fill',
            filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            paint: {
               'fill-color': mapConfig?.primary,
               'fill-opacity': 0.2,
            },
         },
         {
            id: 'gl-draw-polygon-stroke',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            layout: {
               'line-cap': 'round',
               'line-join': 'round',
            },
            paint: {
               'line-color': mapConfig?.primary,
               'line-width': 3,
            },
         },
         {
            id: 'gl-draw-point',
            type: 'circle',
            filter: ['all', ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            paint: {
               'circle-radius': 6,
               'circle-color': mapConfig?.primary,
            },
         },
         {
            id: 'gl-draw-vertex',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
            paint: {
               'circle-radius': 4,
               'circle-color': mapConfig?.primary,
               'circle-stroke-color': '#ffffff',
               'circle-stroke-width': 2,
            },
         },
         {
            id: 'gl-draw-point-active',
            type: 'circle',
            filter: ['all', ['==', '$type', 'Point'], ['==', 'active', 'true']],
            paint: {
               'circle-radius': 6,
               'circle-color': mapConfig?.secondary,
               'circle-stroke-color': '#ffffff',
               'circle-stroke-width': 2,
            },
         },
      ],
   })

   const handleDataChange = () => {
      const data = draw.value!.getAll()
      emit('change', data.features)
   }

   map.value.addControl(draw.value as any)

   map.value.on('draw.create', (e) => {
      emit('create', e.features)
      handleDataChange()
   })
   map.value.on('draw.update', (e) => {
      emit('update', e.features)
      handleDataChange()
   })
   map.value.on('draw.delete', (e) => {
      emit('delete', e.features)
      handleDataChange()
   })

   props.features?.forEach((feature) => {
      if (
         feature.geojson &&
         feature.geojson !== '{}' &&
         typeof feature.geojson === 'object' &&
         feature.geojson.type
      ) {
         addGeometryToMap(draw.value, feature.geojson, feature.id, feature.is_editable !== false)
      }
   })
}

const getEffectiveBbox = () => {
   if (!props.features || props.features.length === 0) return null

   if (
      typeof props.bboxIndex === 'number' &&
      props.bboxIndex >= 0 &&
      props.bboxIndex < props.features.length
   ) {
      const feature = props.features[props.bboxIndex]
      if (feature.bbox && feature.bbox.coordinates) {
         return feature.bbox
      }
   }

   const validFeatures = props.features.filter(
      (feature) =>
         feature.geojson &&
         feature.geojson !== '{}' &&
         typeof feature.geojson === 'object' &&
         feature.geojson.type &&
         feature.geojson.coordinates
   )

   if (validFeatures.length === 0) return null

   const allGeojson = {
      type: 'FeatureCollection' as const,
      features: validFeatures.map((feature) => ({
         type: 'Feature' as const,
         geometry: feature.geojson,
         properties: {},
      })),
   }

   try {
      const bboxArray = bbox(allGeojson)
      return {
         type: 'Polygon',
         coordinates: [
            [
               [bboxArray[0], bboxArray[1]],
               [bboxArray[2], bboxArray[1]],
               [bboxArray[2], bboxArray[3]],
               [bboxArray[0], bboxArray[3]],
               [bboxArray[0], bboxArray[1]],
            ],
         ],
      }
   } catch (error) {
      console.error('Error calculating bbox:', error)
      return null
   }
}

const initMap = () => {
   if (!mapContainer.value) return

   const effectiveBbox = getEffectiveBbox()

   const bounds = getBoundsFromBbox(effectiveBbox, 0.05)
   const fitBounds = getBoundsFromBbox(effectiveBbox, 0)
   const center = bounds
      ? ([(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2] as [number, number])
      : mapConfig.center

   map.value = new maplibregl.Map({
      container: mapContainer.value,
      style: {
         version: 8,
         glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
         sources: {
            osm: {
               type: 'raster',
               tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
               tileSize: 256,
               attribution: '© OpenStreetMap contributors',
            },
         },
         layers: [
            {
               id: 'osm',
               type: 'raster',
               source: 'osm',
            },
         ],
      },
      center,
      zoom: 14,
      minZoom: mapConfig.minZoom,
      maxBounds: mapConfig.limit,
   })

   if (fitBounds) {
      map.value.fitBounds(fitBounds as [number, number, number, number], {
         padding: 50,
         maxZoom: 16,
      })
   }

   map.value.on('load', async () => {
      await loadMapIcons()
      if (props.readonly) {
         addReadonlyGeometries()
      } else {
         initDrawControl()
      }
      if (props.features && props.features.length > 0 && props.showMarkers) {
         await addPointMarkers()
      }
   })

   map.value.on('click', (e) => {
      if (props.readonly) {
         const readonlyFeatures = map.value!.queryRenderedFeatures(e.point, {
            layers: ['readonly-features-fill', 'readonly-features-line'],
         })
         if (readonlyFeatures.length > 0) {
            const feature = readonlyFeatures[0]
            emit('select', feature)
         }
      }
   })
}

const getFeatures = () => {
   return draw.value?.getAll().features || []
}

const clearAll = () => {
   draw.value?.deleteAll()
}

const addFeature = (feature: any) => {
   draw.value?.add(feature)
}

const deleteFeature = (featureId: string) => {
   draw.value?.delete(featureId)
}

const updatePointMarkers = () => {
   if (!map.value || !props.features || !map.value.isStyleLoaded() || !props.showMarkers) return

   const centers = {
      type: 'FeatureCollection' as const,
      features: props.features
         .filter((feature) => {
            return feature.center && feature.center.coordinates
         })
         .map((feature) => ({
            type: 'Feature' as const,
            properties: {
               ...feature,
               geojson: undefined,
               center: undefined,
            },
            geometry: feature.center,
         })),
   }

   if (centers.features.length === 0) {
      customMarkers.value.forEach((marker) => marker.remove())
      customMarkers.value = []
      if (map.value.getSource('markers-clustered')) {
         map.value.removeLayer('unclustered-points')
         map.value.removeLayer('clusters')
         if (map.value.getLayer('cluster-count')) {
            map.value.removeLayer('cluster-count')
         }
         map.value.removeSource('markers-clustered')
      }
      return
   }

   const source = map.value.getSource('markers-clustered') as maplibregl.GeoJSONSource
   if (source) {
      source.setData(centers)
   } else {
      addPointMarkers()
   }
}

const updateMapBounds = () => {
   if (!map.value) return

   const effectiveBbox = getEffectiveBbox()
   const fitBounds = getBoundsFromBbox(effectiveBbox, 0)

   if (fitBounds) {
      map.value.fitBounds(fitBounds as [number, number, number, number], {
         padding: 50,
         maxZoom: 16,
      })
   }
}

defineExpose({
   getFeatures,
   clearAll,
   addFeature,
   deleteFeature,
   updatePointMarkers,
   updateMapBounds,
   map,
   draw,
})

onMounted(() => {
   initMap()
})

onUnmounted(() => {
   customMarkers.value.forEach((marker) => marker.remove())
   customMarkers.value = []
   map.value?.remove()
})

watch(
   () => props.features,
   () => {
      if (map.value && props.features) {
         updatePointMarkers()
      }
   },
   { deep: true }
)

watch(
   () => props.enabledModes,
   (newModes, oldModes) => {
      if (!map.value || props.readonly) return

      const newSorted = [...(newModes || [])].sort().join(',')
      const oldSorted = [...(oldModes || [])].sort().join(',')

      if (newSorted !== oldSorted) {
         clearAll()
         initDrawControl()
      }
   },
   { deep: true }
)

watch(
   () => props.bboxIndex,
   () => {
      if (map.value) {
         updateMapBounds()
      }
   }
)
</script>
